import styled from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { breakpoints } from '@/config/layout'
import { EnvironmentBanner } from '@/modules/shared/components/environment-banner/EnvironmentBanner'
import { useSafeAreaColor } from '@/modules/shared/hooks/useSafeAreaColor'

import type { ReactNode } from 'react'

type AuthLayoutProps = {
  children: ReactNode
  image: string
  /**
   * Header component that is rendered differently depending on screen width
   * When on mobile, this component is rendered above the Image
   * When on desktop, this component is rendered abvoe the Content
   */
  HeaderComponent?: React.ReactNode
}

/**
 * Reusable layout component for authentication pages with image and content sections.
 * Handles responsive design and common styling patterns.
 */
export const AuthLayout = ({ children, image, HeaderComponent }: AuthLayoutProps) => {
  const isMobile = useMediaQuery(breakpoints.toS)
  const safeAreaColor = useSafeAreaColor({ isLoggedInForSomeoneElse: false })

  return (
    <StPageWrapper>
      <StSafeAreaTop $bgColor={safeAreaColor} />
      <EnvironmentBanner />

      <StContainer>
        <StImageContainer image={image}>
          {isMobile && HeaderComponent ? (
            <StHeaderContainer justifyContent='flex-start' alignItems='center'>
              {HeaderComponent}
            </StHeaderContainer>
          ) : null}
        </StImageContainer>

        <StContentContainer>
          <StContent>
            {/* Header Component for Desktop */}
            {!isMobile && HeaderComponent ? (
              <StHeaderContainer justifyContent='flex-start' alignItems='center'>
                {HeaderComponent}
              </StHeaderContainer>
            ) : null}

            {children}
          </StContent>
        </StContentContainer>
      </StContainer>
    </StPageWrapper>
  )
}

// Styled Components
// ------------------------------------------------------------------------- /

const StPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
`

const StSafeAreaTop = styled.div<{ $bgColor: string }>`
  flex-shrink: 0;
  height: var(--safe-area-inset-top);
  background-color: ${({ $bgColor }) => $bgColor};
`

const StContainer = styled(Flex)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space6};
  /* Let the window handle scrolling to enable sticky/fixed sidebar */
  overflow: auto;
  flex-direction: row;

  @media ${breakpoints.toS} {
    flex-direction: column;
  }
`

const StContentContainer = styled(FlexColumn)`
  /* Left column (desktop) */
  flex: 0 0 50vw;
  width: 50vw;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space10};
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4};
  order: 1;
  /* Prevent flex children from overflowing horizontally */
  min-width: 0;

  @media ${breakpoints.toS} {
    flex: 1 1 auto;
    width: 100%;
    order: 2;
  }
`

const StContent = styled(FlexColumn)`
  margin: 0 auto;
  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space12};
  max-width: 560px;
  justify-content: space-between;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space6};

  @media ${breakpoints.toS} {
    margin: 0;
    max-width: 100%;
  }
`

const StImageContainer = styled(FlexColumn)<{ image: string }>`
  flex: 1;
  position: sticky;
  top: 0;
  min-height: 250px;
  background-image: url(${({ image }) => image});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  height: 100vh;
  max-height: 100vh;

  order: 2;

  @media ${breakpoints.toS} {
    position: static;
    object-position: center;
    border-radius: 0 0 80px 0;
    order: 1;
  }
`

const StHeaderContainer = styled(Flex)`
  width: 100%;
  background-color: transparent;

  padding-top: ${({ theme }) => theme.UI.SpacingPx.Space4};
  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space10};

  @media ${breakpoints.toS} {
    padding-left: ${({ theme }) => theme.UI.SpacingPx.Space4};
    padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space5};
  }
`
